import React, { useEffect, useRef, useState } from 'react';
import { apiGet, apiPost } from '../../utils/api';
import { BTN } from '../../constants/buttons';
import {
  TASK_PRIORITIES,
  TASK_PRIORITY_LABELS,
  DAY_NIGHT_OPTIONS,
  INT_EXT_OPTIONS,
} from '../../constants/taskStatuses';
import OwnerSubDeptFields, { FIELD_CLS, LABEL_CLS } from './OwnerSubDeptFields';
import SuggestInput from '../ui/SuggestInput';
import { MODAL_OVERLAY, MODAL_PANEL_MOBILE_CAP } from '../../constants/overlayLayers';

const EMPTY_FORM = {
  title: '',
  description: '',
  sub_department_id: '',
  scenes: '',
  shoot_date: '',
  location_access_date: '',
  prep_date: '',
  strike_date: '',
  day_night: '',
  int_ext: '',
  location: '',
  unit: '',
  primary_effect_type: '',
  priority: 'medium',
  owner_id: '', // '' = unassigned; otherwise a user id string
};

/**
 * "+ New Gag" modal. Title is the only required field; everything else is
 * optional and can be filled in later from the breakdown. Unit and effect
 * inputs are `ui/SuggestInput` (free text + ▾ list) fed by /api/gags/units and
 * /api/gags/effect-types. Server validation errors are shown inline.
 *
 * "Assign to" (Round 2, upgraded Round 3): the linked owner ↔ sub-department
 * pair is the shared OwnerSubDeptFields component — the SAME filtering /
 * fill-when-blank rules as the gag editor (see that file for the rules).
 * Non-managers get Unassigned + "Me" only (take semantics); managers get the
 * filtered department picker (assign semantics, server-validated). deptUsers
 * comes from /api/gags/assignable-users via the page.
 *
 * Props: open, subDepts, canManage, userId, deptUsers, onCancel,
 * onCreated (called after successful POST).
 */
export default function GagCreateModal({ open, subDepts, canManage, userId, deptUsers, onCancel, onCreated }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [units, setUnits] = useState([]);
  const [effectTypes, setEffectTypes] = useState([]);
  const [serverError, setServerError] = useState(null);
  const [busy, setBusy] = useState(false);
  const titleRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setForm(EMPTY_FORM);
    setServerError(null);
    setBusy(false);
    setTimeout(() => titleRef.current?.focus(), 0);
    // Autocomplete sources — best-effort; the suggestion lists are just empty on failure.
    (async () => {
      try {
        const [unitsRes, effectsRes] = await Promise.all([
          apiGet('/api/gags/units'),
          apiGet('/api/gags/effect-types'),
        ]);
        if (unitsRes.ok) {
          const d = await unitsRes.json();
          setUnits(Array.isArray(d) ? d : []);
        }
        if (effectsRes.ok) {
          const d = await effectsRes.json();
          setEffectTypes(Array.isArray(d) ? d : []);
        }
      } catch { /* non-fatal */ }
    })();
  }, [open]);

  // Escape closes the modal (standard dialog behaviour) — but never mid-submit.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      // `defaultPrevented`: an open `ui/SuggestInput` list took this Escape
      // to close itself — it must not also close the modal.
      if (e.key === 'Escape' && !busy && !e.defaultPrevented) onCancel?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy, onCancel]);

  if (!open) return null;

  const set = (field) => (e) => setForm((p) => ({ ...p, [field]: e.target.value }));
  // `ui/SuggestInput` hands back a STRING, not an event.
  const setValue = (field) => (v) => setForm((p) => ({ ...p, [field]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (!form.title.trim()) {
      setServerError('Title is required');
      return;
    }
    setBusy(true);
    setServerError(null);
    try {
      // owner_id only when actually picked — the server treats ''/null as
      // unassigned anyway, but omitting keeps the payload honest.
      const { owner_id, ...rest } = form;
      const payload = {
        ...rest,
        title: form.title.trim(),
        sub_department_id: form.sub_department_id || null,
      };
      if (owner_id) payload.owner_id = parseInt(owner_id, 10);
      const res = await apiPost('/api/gags', payload);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setServerError(data.error || `Failed to create gag (HTTP ${res.status})`);
        setBusy(false);
        return;
      }
      onCreated?.(data);
    } catch (err) {
      setServerError('Failed to create gag — check your connection and try again.');
      setBusy(false);
    }
  };

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 ${MODAL_OVERLAY}`}
      onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onCancel?.(); }}
      role="dialog"
      aria-modal="true"
      aria-label="New gag"
    >
      <form
        onSubmit={submit}
        className={`bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-5 ${MODAL_PANEL_MOBILE_CAP}`}
      >
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">New Gag</h2>

        {serverError && (
          <div className="mb-3 px-3 py-2 text-sm rounded-lg bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800">
            {serverError}
          </div>
        )}

        <div className="space-y-3">
          <div>
            <label className={LABEL_CLS}>Title *</label>
            <input ref={titleRef} type="text" value={form.title} onChange={set('title')}
              maxLength={200} required disabled={busy} className={FIELD_CLS} />
          </div>

          <div>
            <label className={LABEL_CLS}>Description</label>
            <textarea value={form.description} onChange={set('description')} rows={3}
              disabled={busy} className={`${FIELD_CLS} resize-y`} />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {/* Linked Sub-department ↔ Assign-to pair (Round 3) — shared
                component, same rules as the gag editor. */}
            <OwnerSubDeptFields
              subDepts={subDepts}
              users={deptUsers}
              canManage={canManage}
              userId={userId}
              subDeptValue={form.sub_department_id}
              ownerValue={form.owner_id}
              currentOwner={null}
              onPatch={(patch) => setForm((p) => ({ ...p, ...patch }))}
              disabled={busy}
              ownerLabel="Assign to"
            />
            <div>
              <label className={LABEL_CLS}>Priority</label>
              <select value={form.priority} onChange={set('priority')} disabled={busy} className={FIELD_CLS}>
                {TASK_PRIORITIES.map((p) => (
                  <option key={p} value={p}>{TASK_PRIORITY_LABELS[p]}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className={LABEL_CLS}>Scenes</label>
            <input type="text" value={form.scenes} onChange={set('scenes')} disabled={busy}
              placeholder="e.g. 45, 47A, 102" className={FIELD_CLS} />
            <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
              Comma-separated scene numbers — the gag will appear under each scene when grouping by scene.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <label className={LABEL_CLS}>Shoot date</label>
              <input type="date" value={form.shoot_date} onChange={set('shoot_date')} disabled={busy} className={FIELD_CLS} />
            </div>
            <div>
              <label className={LABEL_CLS}>Location access</label>
              <input type="date" value={form.location_access_date} onChange={set('location_access_date')} disabled={busy} className={FIELD_CLS} />
            </div>
            <div>
              <label className={LABEL_CLS}>Prep date</label>
              <input type="date" value={form.prep_date} onChange={set('prep_date')} disabled={busy} className={FIELD_CLS} />
            </div>
            <div>
              <label className={LABEL_CLS}>Strike date</label>
              <input type="date" value={form.strike_date} onChange={set('strike_date')} disabled={busy} className={FIELD_CLS} />
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <label className={LABEL_CLS}>Day / Night</label>
              <select value={form.day_night} onChange={set('day_night')} disabled={busy} className={FIELD_CLS}>
                <option value="">-</option>
                {DAY_NIGHT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={LABEL_CLS}>INT / EXT</label>
              <select value={form.int_ext} onChange={set('int_ext')} disabled={busy} className={FIELD_CLS}>
                <option value="">-</option>
                {INT_EXT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={LABEL_CLS}>Unit</label>
              <SuggestInput value={form.unit} onChange={setValue('unit')} disabled={busy}
                options={units} placeholder="e.g. Main Unit" inputClassName={FIELD_CLS}
                ariaLabel="Unit" listLabel="Units" testId="gag-create-unit" />
            </div>
            <div>
              <label className={LABEL_CLS}>Primary effect</label>
              <SuggestInput value={form.primary_effect_type} onChange={setValue('primary_effect_type')} disabled={busy}
                options={effectTypes} placeholder="e.g. Rain" inputClassName={FIELD_CLS}
                ariaLabel="Primary effect" listLabel="Effect types" testId="gag-create-effect" />
            </div>
          </div>

          <div>
            <label className={LABEL_CLS}>Location</label>
            <input type="text" value={form.location} onChange={set('location')} disabled={busy}
              placeholder="e.g. Stage 4 — tank" className={FIELD_CLS} />
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-5">
          <button type="button" onClick={() => onCancel?.()} disabled={busy} className={BTN.secondary}>
            Cancel
          </button>
          <button type="submit" disabled={busy || !form.title.trim()} className={BTN.primary}>
            {busy ? 'Creating…' : 'Create Gag'}
          </button>
        </div>
      </form>
    </div>
  );
}
